// Sugar Alarm — cycle 452.2: ONE look for every alarm banner.
//
// The Low / High / Urgent banners Now mounts and the off-Now banner the shell
// mounts (`CompactAlarmControl`) read their ground, ink, row height and button
// geometry from HERE, so the banner a caregiver sees off Now is the banner
// they see on Now and the two cannot drift. (The off-Now banner cannot mount
// Now's banner COMPONENTS themselves: their mount gates and action sets are
// Now's — an active urgent low is Now's full-screen takeover, not a banner —
// so it shares their styles, which is the fallback the cycle brief allows.)
//
// ⚠ TWO RULES THIS FILE EXISTS TO HOLD (operator decisions, 2026-09-25):
//   1. Every alarm button is at least 44 px tall — the hit area and the drawn
//      pill both. The banner row grows to carry it (`ALARM_BANNER_ROW_PX`).
//   2. Every alarm-control text reads at 4.5:1 or better against its own
//      backing, in every state and every theme. The grounds are OPAQUE (the
//      `--alarm-*` tokens in theme/tokens.css) and nothing here dims text with
//      `opacity` — a dimmed label is a lower-contrast label.
// `alarmBanner.test.ts` pins both in the source; the type-floor sweep measures
// them on what actually renders.

import type { CSSProperties } from "react";

import type { AlertLevel } from "../../lib/types";

/** The WCAG-minimum touch target, and the floor for every alarm button. */
export const ALARM_BUTTON_MIN_PX = 44;
/** Vertical padding around the buttons inside a banner row. */
export const ALARM_BANNER_PAD_Y = 4;
/** A single-line banner row: the button plus its padding. Mirrors
 *  `--kiosk-alarm-banner-height` in theme/tokens.css. */
export const ALARM_BANNER_ROW_PX = ALARM_BUTTON_MIN_PX + 2 * ALARM_BANNER_PAD_Y;

/** Amber for Low / High, red for the urgent family. */
export type AlarmTone = "warn" | "urgent";

export function alarmTone(level: AlertLevel | null | undefined): AlarmTone {
  return level === "urgent_low" || level === "urgent_high" ? "urgent" : "warn";
}

/** Ink on the white Snooze pill (8.6:1 and 9.4:1 on white). */
const PRIMARY_INK: Record<AlarmTone, string> = {
  warn: "#7a3a06",
  urgent: "#8a1a1a",
};

/** The Snooze pill's ink, for a surface that sizes its own pill (the
 *  bedside takeover). */
export function alarmPrimaryInk(tone: AlarmTone): string {
  return PRIMARY_INK[tone];
}

/** The banner's ground and ink. The row's own layout stays with each banner
 *  (the kiosk strip is a fixed-height row, the phone strip may wrap). */
export function alarmBannerGround(tone: AlarmTone): CSSProperties {
  return {
    background: `var(--alarm-${tone}-fill)`,
    color: `var(--alarm-${tone}-ink)`,
    // The kiosk `.alarmBannerStrip` rule adds a dark text-shadow as a
    // legibility net for WHITE copy over a translucent fill. The fills are
    // opaque now, and a dark shadow under dark ink only blurs it.
    textShadow: "none",
  };
}

/** The pulsing status dot, drawn in the banner's own ink. */
export function alarmBannerDot(pulsing: boolean): CSSProperties {
  return {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "currentColor",
    flexShrink: 0,
    // A muted (stopped) strip keeps a still, softer dot — the dot is a glyph,
    // not text, and the words next to it carry the state.
    animation: pulsing ? "pulseDot 1.1s ease-in-out infinite" : undefined,
    opacity: pulsing ? 1 : 0.6,
  };
}

/** Snooze: the solid white pill. */
export function alarmPrimaryButton(tone: AlarmTone): CSSProperties {
  return {
    appearance: "none",
    boxSizing: "border-box",
    border: 0,
    background: "#fff",
    color: PRIMARY_INK[tone],
    borderRadius: 999,
    padding: "0 16px",
    fontWeight: 800,
    fontSize: "var(--type-floor)",
    letterSpacing: "0.04em",
    cursor: "pointer",
    textTransform: "uppercase",
    minHeight: ALARM_BUTTON_MIN_PX,
    // Cycle 187 / 62: the urgent Snooze keeps its comfortable width.
    minWidth: tone === "urgent" ? 64 : ALARM_BUTTON_MIN_PX,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
    fontFamily: "inherit",
    textShadow: "none",
  };
}

/** Stop, Go to alarm, Sound the alarm now: outlined in the banner's ink. */
export function alarmSecondaryButton(): CSSProperties {
  return {
    appearance: "none",
    boxSizing: "border-box",
    border: "1.5px solid currentColor",
    background: "transparent",
    color: "inherit",
    borderRadius: 999,
    padding: "0 14px",
    fontWeight: 700,
    fontSize: "var(--type-floor)",
    letterSpacing: "0.06em",
    cursor: "pointer",
    textTransform: "uppercase",
    minHeight: ALARM_BUTTON_MIN_PX,
    minWidth: ALARM_BUTTON_MIN_PX,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    textShadow: "none",
  };
}
